import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { auth, firestore } from '../firebaseConfig';
import { collection, doc, getDoc, getDocs, query, where, limit, setDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { buildSearchTerms } from './searchUtils';
import { computeDomainDedup } from '../shared/dedupNormalize';
import useCustomFields from '../hooks/useCustomFields';
import './WorldDbCreator.css';

// Mirrors the importer's normalization so a contact created manually and the
// same contact uploaded via CSV resolve to the same Firestore doc ID and the
// same dedup fields. Without this, manual entries would not be visible to the
// duplicate check in WorldDbUploader.
const normalizeEmailForComparison = (email) => {
  if (!email || typeof email !== 'string') return '';
  return email
    .trim()
    .toLowerCase()
    .normalize('NFKC')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const normalizeBusinessName = (name) => {
  if (!name || typeof name !== 'string') return '';
  return name
    .trim()
    .toLowerCase()
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, ' ');
};

const isValidDocId = (id) => {
  if (!id || typeof id !== 'string') return false;
  if (id.includes('/')) return false;
  if (id === '.' || id === '..') return false;
  if (new Blob([id]).size > 1500) return false;
  return true;
};

const isValidEmail = (email) => {
  if (!email || typeof email !== 'string') return false;
  const trimmed = email.trim();
  if (!trimmed) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
};

const NUMERIC_FIELDS = ['lead_rating', 'lead_reviews', 'lead_stars', 'lead_instagram_followers'];

const SOCIAL_FIELD_MAP = {
  lead_linkedin: 'linkedin',
  lead_instagram: 'instagram',
  lead_facebook: 'facebook',
  lead_tiktok: 'tiktok',
  lead_twitter: 'x',
  lead_youtube: 'youtube',
  lead_xing: 'xing',
  lead_website: 'website',
};

const EMPTY_FORM = {
  business_name: '',
  lead_email: '',
  lead_telephone: '',
  lead_website: '',
  lead_description: '',
  lead_address: '',
  lead_city: '',
  lead_postcode: '',
  lead_country: '',
  lead_instagram: '',
  lead_linkedin: '',
  lead_facebook: '',
  lead_tiktok: '',
  lead_twitter: '',
  lead_youtube: '',
  lead_xing: '',
  lead_rating: '',
  lead_reviews: '',
  lead_stars: '',
  lead_instagram_followers: '',
};

const EMPTY_DM = { name: '', role: '', email: '', phone: '' };

const WorldDbCreator = ({ onClose, onCreated, userId: propUserId }) => {
  const [authUserId, setAuthUserId] = useState(null);
  const userId = propUserId || authUserId;

  const [form, setForm] = useState(EMPTY_FORM);
  const [customValues, setCustomValues] = useState({});
  const [decisionMaker, setDecisionMaker] = useState(EMPTY_DM);

  // Optional field-groups: hidden by default; the user opts in via the
  // "Add more fields" picker below the contact channels. This keeps the
  // default form short and focused on the most-used fields.
  const [addedSections, setAddedSections] = useState({
    location: false,
    social: false,
    metrics: false,
    decisionMaker: false,
  });

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  // Per-field duplicate-check state. Each carries one of:
  //   'idle'       — input empty or below the validation threshold
  //   'checking'   — Firestore query in flight (debounced)
  //   'available'  — no match in the user's db
  //   'exists'     — a contact with the same dedup key already exists
  //   'invalid'    — input is not a syntactically valid email (email only)
  // `label` holds the existing contact's display name so we can surface it
  // ("Already exists as Acme GmbH").
  const [emailCheck, setEmailCheck] = useState({ status: 'idle', label: '' });
  const [businessCheck, setBusinessCheck] = useState({ status: 'idle', label: '' });

  const modalRef = useRef(null);
  const businessInputRef = useRef(null);
  const emailDebounceRef = useRef(null);
  const businessDebounceRef = useRef(null);

  const { customFields } = useCustomFields(userId);

  // Fall back to current auth user if no userId was passed in (keeps the
  // component usable outside of WorldDb.js, e.g. on the empty-state screen).
  useEffect(() => {
    if (propUserId) return undefined;
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthUserId(user ? user.uid : null);
    });
    return () => unsubscribe();
  }, [propUserId]);

  // Close on outside-click + ESC. We avoid stopping propagation inside the
  // modal so the user can still interact with select boxes inside it.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (saving) return;
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    const handleEsc = (event) => {
      if (event.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose, saving]);

  // Autofocus the first field for fast keyboard entry.
  useEffect(() => {
    const t = setTimeout(() => businessInputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  const updateField = useCallback((key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (error) setError('');
  }, [error]);

  const updateDm = useCallback((key, value) => {
    setDecisionMaker(prev => ({ ...prev, [key]: value }));
  }, []);

  const updateCustom = useCallback((key, value) => {
    setCustomValues(prev => ({ ...prev, [key]: value }));
  }, []);

  const addSection = (key) => {
    setAddedSections(prev => ({ ...prev, [key]: true }));
  };

  // Removing a section also wipes its inputs so we don't accidentally save
  // values from a section the user explicitly removed.
  const removeSection = (key) => {
    setAddedSections(prev => ({ ...prev, [key]: false }));
    if (key === 'location') {
      setForm(prev => ({ ...prev, lead_address: '', lead_city: '', lead_postcode: '', lead_country: '' }));
    } else if (key === 'social') {
      setForm(prev => ({
        ...prev,
        lead_instagram: '', lead_linkedin: '', lead_facebook: '', lead_tiktok: '',
        lead_twitter: '', lead_youtube: '', lead_xing: '',
      }));
    } else if (key === 'metrics') {
      setForm(prev => ({
        ...prev,
        lead_rating: '', lead_reviews: '', lead_stars: '', lead_instagram_followers: '',
      }));
    } else if (key === 'decisionMaker') {
      setDecisionMaker(EMPTY_DM);
    }
  };

  const SECTION_DEFINITIONS = [
    { key: 'location', label: 'Address', icon: 'address' },
    { key: 'social', label: 'Social Media', icon: 'social' },
    { key: 'metrics', label: 'Metrics', icon: 'metrics' },
    { key: 'decisionMaker', label: 'Decision Maker', icon: 'person' },
  ];
  const availableSections = SECTION_DEFINITIONS.filter(s => !addedSections[s.key]);

  // === Duplicate-check helpers =====================================
  // Strategy:
  //   1. Try the deterministic doc id (`e_${email}` / `b_${biz}`) — covers
  //      every contact written by WorldDbUploader.confirmAndImport or by a
  //      previous run of this creator (single round-trip, 0 reads if missing).
  //   2. Fall back to a `where('..._dedup', '==', normalized)` query — this
  //      catches legacy docs that have auto-generated ids but were backfilled
  //      with the dedup fields by `backfillDedupFields`.
  // Together this mirrors the importer's "Check Duplicates" stage so the same
  // contact cannot slip through manual entry.

  const lookupExistingByEmail = useCallback(async (email) => {
    if (!userId) return null;
    const normalized = normalizeEmailForComparison(email);
    if (!normalized) return null;

    const directRef = doc(firestore, `World/${userId}/db`, `e_${normalized}`);
    const directSnap = await getDoc(directRef);
    if (directSnap.exists()) return directSnap.data();

    const q = query(
      collection(firestore, `World/${userId}/db`),
      where('lead_email_dedup', '==', normalized),
      limit(1)
    );
    const snap = await getDocs(q);
    if (!snap.empty) return snap.docs[0].data();
    return null;
  }, [userId]);

  const lookupExistingByBusinessName = useCallback(async (businessName) => {
    if (!userId) return null;
    const normalized = normalizeBusinessName(businessName);
    if (!normalized) return null;

    const directRef = doc(firestore, `World/${userId}/db`, `b_${normalized}`);
    const directSnap = await getDoc(directRef);
    if (directSnap.exists()) return directSnap.data();

    const q = query(
      collection(firestore, `World/${userId}/db`),
      where('business_name_dedup', '==', normalized),
      limit(1)
    );
    const snap = await getDocs(q);
    if (!snap.empty) return snap.docs[0].data();
    return null;
  }, [userId]);

  const labelForExisting = (data, fallback) => {
    if (!data) return fallback || '';
    return data.business_name || data.lead_email || fallback || '';
  };

  // Live email-check: debounced 400ms after the last keystroke so the user
  // gets fast feedback without spamming Firestore on every character.
  useEffect(() => {
    if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current);

    const raw = (form.lead_email || '').trim();
    if (!raw) {
      setEmailCheck({ status: 'idle', label: '' });
      return undefined;
    }
    if (!isValidEmail(raw)) {
      setEmailCheck({ status: 'invalid', label: '' });
      return undefined;
    }
    if (!userId) return undefined;

    setEmailCheck({ status: 'checking', label: '' });

    emailDebounceRef.current = setTimeout(async () => {
      try {
        const existing = await lookupExistingByEmail(raw);
        if (existing) {
          setEmailCheck({
            status: 'exists',
            label: labelForExisting(existing, raw),
          });
        } else {
          setEmailCheck({ status: 'available', label: '' });
        }
      } catch (err) {
        console.warn('[WorldDbCreator] email duplicate check failed:', err);
        setEmailCheck({ status: 'idle', label: '' });
      }
    }, 400);

    return () => {
      if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current);
    };
  }, [form.lead_email, userId, lookupExistingByEmail]);

  // Live business-name check, same pattern as email.
  useEffect(() => {
    if (businessDebounceRef.current) clearTimeout(businessDebounceRef.current);

    const raw = (form.business_name || '').trim();
    if (raw.length < 2) {
      setBusinessCheck({ status: 'idle', label: '' });
      return undefined;
    }
    if (!userId) return undefined;

    setBusinessCheck({ status: 'checking', label: '' });

    businessDebounceRef.current = setTimeout(async () => {
      try {
        const existing = await lookupExistingByBusinessName(raw);
        if (existing) {
          setBusinessCheck({
            status: 'exists',
            label: labelForExisting(existing, raw),
          });
        } else {
          setBusinessCheck({ status: 'available', label: '' });
        }
      } catch (err) {
        console.warn('[WorldDbCreator] business-name duplicate check failed:', err);
        setBusinessCheck({ status: 'idle', label: '' });
      }
    }, 400);

    return () => {
      if (businessDebounceRef.current) clearTimeout(businessDebounceRef.current);
    };
  }, [form.business_name, userId, lookupExistingByBusinessName]);

  const hasDuplicate = emailCheck.status === 'exists' || businessCheck.status === 'exists';
  const isChecking = emailCheck.status === 'checking' || businessCheck.status === 'checking';

  const canSubmit = useMemo(() => {
    const hasEmail = isValidEmail(form.lead_email);
    const hasBusiness = (form.business_name || '').trim().length > 0;
    if (!hasEmail && !hasBusiness) return false;
    if (saving) return false;
    if (hasDuplicate) return false;
    if (isChecking) return false;
    return true;
  }, [form.lead_email, form.business_name, saving, hasDuplicate, isChecking]);

  // Build the payload exactly like WorldDbUploader.confirmAndImport so the new
  // record is consistent with imported ones — same dedup fields, same
  // numeric coercion, same lead_socials JSON, same trigram search_terms.
  const buildPayload = useCallback(() => {
    const row = {};

    Object.entries(form).forEach(([key, value]) => {
      if (NUMERIC_FIELDS.includes(key)) {
        const parsed = parseFloat(value);
        row[key] = Number.isNaN(parsed) ? 0 : parsed;
      } else {
        row[key] = typeof value === 'string' ? value.trim() : value;
      }
    });

    Object.entries(customValues).forEach(([key, value]) => {
      if (typeof value === 'string' && value.trim() !== '') {
        row[key] = value.trim();
      }
    });

    const trimmedEmail = (row.lead_email || '').toLowerCase();
    const hasEmail = isValidEmail(trimmedEmail);
    row.lead_email = hasEmail ? trimmedEmail : '';

    const hasWebsite = !!(row.lead_website && row.lead_website.trim());
    const hasInstagram = !!(row.lead_instagram && row.lead_instagram.trim());

    const socialsObj = {};
    Object.entries(SOCIAL_FIELD_MAP).forEach(([field, key]) => {
      if (row[field] && row[field].trim()) socialsObj[key] = row[field].trim();
    });
    if (Object.keys(socialsObj).length > 0) {
      row.lead_socials = JSON.stringify(socialsObj);
    }

    const dmTrimmed = {
      name: (decisionMaker.name || '').trim(),
      role: (decisionMaker.role || '').trim(),
      email: (decisionMaker.email || '').trim(),
      phone: (decisionMaker.phone || '').trim(),
    };
    if (dmTrimmed.name) {
      const dm = { name: dmTrimmed.name };
      if (dmTrimmed.role) dm.role = dmTrimmed.role;
      if (dmTrimmed.email) dm.email = dmTrimmed.email;
      if (dmTrimmed.phone) dm.phone = dmTrimmed.phone;
      row.lead_decision_makers = JSON.stringify([dm]);
    }

    row.has_email = hasEmail;
    row.has_website = hasWebsite;
    row.has_instagram = hasInstagram;
    row.is_ready_for_spy = hasWebsite && !hasEmail;

    row.business_name_normalized = row.business_name ? row.business_name.toLowerCase() : '';
    row.lead_email_dedup = hasEmail ? normalizeEmailForComparison(trimmedEmail) : '';
    row.business_name_dedup = row.business_name ? normalizeBusinessName(row.business_name) : '';
    // Phase 0 (additiv): normalisierte Firmen-Domain für künftiges Domain-Dedup.
    row.lead_domain_dedup = computeDomainDedup(row);

    row.search_terms = buildSearchTerms(row);

    row.lead_contacted = 'Not Contacted';
    row.flow_sort_priority = 3;
    row.timestamp = new Date().toISOString();
    row.import_name = 'manual_entry';

    return row;
  }, [form, customValues, decisionMaker]);

  const resolveDocId = useCallback((row) => {
    const emailDocId = row.lead_email_dedup ? `e_${row.lead_email_dedup}` : null;
    const bizDocId = row.business_name_dedup ? `b_${row.business_name_dedup}` : null;
    if (emailDocId && isValidDocId(emailDocId)) return emailDocId;
    if (bizDocId && isValidDocId(bizDocId)) return bizDocId;
    return null;
  }, []);

  const handleSave = useCallback(async () => {
    setError('');

    if (!userId) {
      setError('You are not signed in. Please reload the page.');
      return;
    }

    const hasEmailInput = (form.lead_email || '').trim().length > 0;
    if (hasEmailInput && !isValidEmail(form.lead_email)) {
      setError('Please enter a valid email address.');
      return;
    }

    const hasBusiness = (form.business_name || '').trim().length > 0;
    if (!hasEmailInput && !hasBusiness) {
      setError('Please provide at least an email or a business name.');
      return;
    }

    setSaving(true);

    try {
      // Final duplicate sweep — runs even if the live-check already cleared
      // both fields, in case a teammate added the same contact in the seconds
      // between focus-out and pressing "Save".
      const [existingByEmail, existingByBusiness] = await Promise.all([
        hasEmailInput ? lookupExistingByEmail(form.lead_email) : Promise.resolve(null),
        hasBusiness ? lookupExistingByBusinessName(form.business_name) : Promise.resolve(null),
      ]);

      if (existingByEmail) {
        const label = labelForExisting(existingByEmail, form.lead_email);
        setEmailCheck({ status: 'exists', label });
        setError(`A contact with this email already exists ("${label}"). Adding duplicates is blocked.`);
        setSaving(false);
        return;
      }
      if (existingByBusiness) {
        const label = labelForExisting(existingByBusiness, form.business_name);
        setBusinessCheck({ status: 'exists', label });
        setError(`A contact with this business name already exists ("${label}"). Adding duplicates is blocked.`);
        setSaving(false);
        return;
      }

      const row = buildPayload();
      const docId = resolveDocId(row);

      const docRef = docId
        ? doc(firestore, `World/${userId}/db`, docId)
        : doc(collection(firestore, `World/${userId}/db`));

      await setDoc(docRef, row, { merge: true });

      // Also keep the "manual_entry" entry in the import_names list so the
      // contact shows up under that filter just like CSV imports do.
      try {
        const importNameRef = doc(firestore, `World/${userId}/import_names`, 'manual_entry');
        await setDoc(importNameRef, {
          name: 'manual_entry',
          last_imported: new Date().toISOString(),
          source: 'manual',
        }, { merge: true });
      } catch (_) { /* metadata-only — never block the save */ }

      setSuccess(true);
      setSaving(false);
      setTimeout(() => {
        if (onCreated) onCreated();
        else onClose();
      }, 900);
    } catch (err) {
      console.error('[WorldDbCreator] save failed:', err);
      setError('Could not save the contact. Please try again.');
      setSaving(false);
    }
  }, [
    userId,
    form,
    buildPayload,
    resolveDocId,
    onCreated,
    onClose,
    lookupExistingByEmail,
    lookupExistingByBusinessName,
  ]);

  const fieldLabels = {
    business_name: 'Business Name',
    lead_email: 'Email',
    lead_telephone: 'Phone',
    lead_website: 'Website',
    lead_description: 'Description / Notes',
    lead_address: 'Street Address',
    lead_city: 'City',
    lead_postcode: 'Postcode',
    lead_country: 'Country',
    lead_instagram: 'Instagram',
    lead_linkedin: 'LinkedIn',
    lead_facebook: 'Facebook',
    lead_tiktok: 'TikTok',
    lead_twitter: 'X / Twitter',
    lead_youtube: 'YouTube',
    lead_xing: 'Xing',
    lead_rating: 'Total Rating',
    lead_reviews: 'Total Reviews',
    lead_stars: 'Stars',
    lead_instagram_followers: 'Instagram Followers',
  };

  const renderInput = (key, type = 'text', placeholder = '') => (
    <input
      type={type}
      className="bcWdbCreator__input"
      value={form[key] ?? ''}
      onChange={(e) => updateField(key, e.target.value)}
      placeholder={placeholder || fieldLabels[key]}
      autoComplete="off"
    />
  );

  if (success) {
    return (
      <div className="bcWdbCreator__overlay">
        <div className="bcWdbCreator__container" ref={modalRef} role="dialog" aria-modal="true">
          <div className="bcWdbCreator__success">
            <div className="bcWdbCreator__success-icon">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                <path d="M20 6L9 17l-5-5" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 className="bcWdbCreator__success-title">Contact added</h2>
            <p className="bcWdbCreator__success-text">
              The new contact has been saved to your database.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bcWdbCreator__overlay">
      <div
        className="bcWdbCreator__container"
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bcWdbCreator__title"
      >
        <button
          className="bcWdbCreator__close-button"
          onClick={onClose}
          type="button"
          aria-label="Close"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <header className="bcWdbCreator__header">
          <div className="bcWdbCreator__header-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
              <path d="M19 8v6M22 11h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
          <div>
            <h2 id="bcWdbCreator__title" className="bcWdbCreator__title">Add Contact</h2>
            <p className="bcWdbCreator__subtitle">
              Provide at least an email or a business name. Everything else is optional.
            </p>
          </div>
        </header>

        {error && (
          <div className="bcWdbCreator__error" role="alert">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
              <path d="M12 8v5M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        <div className="bcWdbCreator__body">
          {/* === Identity ============================================== */}
          <section className="bcWdbCreator__section">
            <div className="bcWdbCreator__section-header">
              <span className="bcWdbCreator__section-title">Identity</span>
              <span className="bcWdbCreator__section-hint">At least one of the following is required</span>
            </div>

            <div className="bcWdbCreator__grid two-col">
              <div className="bcWdbCreator__field">
                <label className="bcWdbCreator__field-label">
                  {fieldLabels.business_name}
                </label>
                <input
                  ref={businessInputRef}
                  type="text"
                  className={`bcWdbCreator__input ${businessCheck.status === 'exists' ? 'has-duplicate' : ''}`}
                  value={form.business_name}
                  onChange={(e) => updateField('business_name', e.target.value)}
                  placeholder="e.g. Acme GmbH"
                  autoComplete="off"
                />
                <DuplicateHint check={businessCheck} kind="business" />
              </div>

              <div className="bcWdbCreator__field">
                <label className="bcWdbCreator__field-label">{fieldLabels.lead_email}</label>
                <input
                  type="email"
                  className={`bcWdbCreator__input ${emailCheck.status === 'exists' ? 'has-duplicate' : ''}`}
                  value={form.lead_email}
                  onChange={(e) => updateField('lead_email', e.target.value)}
                  placeholder="name@company.com"
                  autoComplete="off"
                />
                <DuplicateHint check={emailCheck} kind="email" />
              </div>
            </div>

            <div className="bcWdbCreator__field">
              <label className="bcWdbCreator__field-label">{fieldLabels.lead_description}</label>
              <textarea
                className="bcWdbCreator__input bcWdbCreator__textarea"
                value={form.lead_description}
                onChange={(e) => updateField('lead_description', e.target.value)}
                placeholder="Short note that helps the AI personalize emails…"
                rows={3}
              />
            </div>
          </section>

          {/* === Contact channels ===================================== */}
          <section className="bcWdbCreator__section">
            <div className="bcWdbCreator__section-header">
              <span className="bcWdbCreator__section-title">Contact channels</span>
            </div>
            <div className="bcWdbCreator__grid two-col">
              <div className="bcWdbCreator__field">
                <label className="bcWdbCreator__field-label">{fieldLabels.lead_telephone}</label>
                {renderInput('lead_telephone', 'tel', '+49 …')}
              </div>
              <div className="bcWdbCreator__field">
                <label className="bcWdbCreator__field-label">{fieldLabels.lead_website}</label>
                {renderInput('lead_website', 'url', 'https://…')}
              </div>
            </div>
          </section>

          {/* === Optional sections (added on demand) ================= */}
          {addedSections.location && (
            <OptionalSection title="Address" onRemove={() => removeSection('location')}>
              <div className="bcWdbCreator__field">
                <label className="bcWdbCreator__field-label">{fieldLabels.lead_address}</label>
                {renderInput('lead_address')}
              </div>
              <div className="bcWdbCreator__grid three-col">
                <div className="bcWdbCreator__field">
                  <label className="bcWdbCreator__field-label">{fieldLabels.lead_postcode}</label>
                  {renderInput('lead_postcode')}
                </div>
                <div className="bcWdbCreator__field">
                  <label className="bcWdbCreator__field-label">{fieldLabels.lead_city}</label>
                  {renderInput('lead_city')}
                </div>
                <div className="bcWdbCreator__field">
                  <label className="bcWdbCreator__field-label">{fieldLabels.lead_country}</label>
                  {renderInput('lead_country')}
                </div>
              </div>
            </OptionalSection>
          )}

          {addedSections.social && (
            <OptionalSection title="Social Media" onRemove={() => removeSection('social')}>
              <div className="bcWdbCreator__grid two-col">
                {['lead_instagram', 'lead_linkedin', 'lead_facebook', 'lead_tiktok', 'lead_twitter', 'lead_youtube', 'lead_xing'].map((key) => (
                  <div key={key} className="bcWdbCreator__field">
                    <label className="bcWdbCreator__field-label">{fieldLabels[key]}</label>
                    {renderInput(key, 'url', 'https://…')}
                  </div>
                ))}
              </div>
            </OptionalSection>
          )}

          {addedSections.metrics && (
            <OptionalSection title="Metrics" onRemove={() => removeSection('metrics')}>
              <div className="bcWdbCreator__grid two-col">
                {NUMERIC_FIELDS.map((key) => (
                  <div key={key} className="bcWdbCreator__field">
                    <label className="bcWdbCreator__field-label">{fieldLabels[key]}</label>
                    {renderInput(key, 'number', '0')}
                  </div>
                ))}
              </div>
            </OptionalSection>
          )}

          {addedSections.decisionMaker && (
            <OptionalSection title="Decision Maker" onRemove={() => removeSection('decisionMaker')}>
              <div className="bcWdbCreator__grid two-col">
                <div className="bcWdbCreator__field">
                  <label className="bcWdbCreator__field-label">Name</label>
                  <input
                    type="text"
                    className="bcWdbCreator__input"
                    value={decisionMaker.name}
                    onChange={(e) => updateDm('name', e.target.value)}
                    placeholder="Full name"
                  />
                </div>
                <div className="bcWdbCreator__field">
                  <label className="bcWdbCreator__field-label">Position</label>
                  <input
                    type="text"
                    className="bcWdbCreator__input"
                    value={decisionMaker.role}
                    onChange={(e) => updateDm('role', e.target.value)}
                    placeholder="e.g. CEO"
                  />
                </div>
                <div className="bcWdbCreator__field">
                  <label className="bcWdbCreator__field-label">Email</label>
                  <input
                    type="email"
                    className="bcWdbCreator__input"
                    value={decisionMaker.email}
                    onChange={(e) => updateDm('email', e.target.value)}
                    placeholder="name@company.com"
                  />
                </div>
                <div className="bcWdbCreator__field">
                  <label className="bcWdbCreator__field-label">Phone</label>
                  <input
                    type="tel"
                    className="bcWdbCreator__input"
                    value={decisionMaker.phone}
                    onChange={(e) => updateDm('phone', e.target.value)}
                    placeholder="+49 …"
                  />
                </div>
              </div>
            </OptionalSection>
          )}

          {/* === "Add more fields" picker ============================ */}
          {availableSections.length > 0 && (
            <div className="bcWdbCreator__add-row" role="group" aria-label="Add more fields">
              <span className="bcWdbCreator__add-row-label">Add more fields</span>
              <div className="bcWdbCreator__add-row-chips">
                {availableSections.map(section => (
                  <button
                    key={section.key}
                    type="button"
                    className="bcWdbCreator__add-chip"
                    onClick={() => addSection(section.key)}
                  >
                    <SectionChipIcon name={section.icon} />
                    <span>{section.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* === Custom fields ====================================== */}
          {customFields && customFields.length > 0 && (
            <section className="bcWdbCreator__section">
              <div className="bcWdbCreator__section-header">
                <span className="bcWdbCreator__section-title">Custom fields</span>
              </div>
              <div className="bcWdbCreator__grid two-col">
                {customFields.map((cf) => (
                  <div key={cf.key} className="bcWdbCreator__field">
                    <label className="bcWdbCreator__field-label">{cf.label}</label>
                    <input
                      type={cf.fieldType === 'number' ? 'number' : 'text'}
                      className="bcWdbCreator__input"
                      value={customValues[cf.key] || ''}
                      onChange={(e) => updateCustom(cf.key, e.target.value)}
                      placeholder={cf.label}
                    />
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        <footer className="bcWdbCreator__footer">
          <button
            type="button"
            className="bcWdbCreator__cancel"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`bcWdbCreator__save ${saving ? 'is-saving' : ''}`}
            onClick={handleSave}
            disabled={!canSubmit}
            title={
              hasDuplicate
                ? 'A contact with the same email or business name already exists.'
                : isChecking
                  ? 'Checking for duplicates…'
                  : ''
            }
          >
            <span className="bcWdbCreator__saveLabel">
              {isChecking ? 'Checking…' : 'Save Contact'}
            </span>
            {saving && <span className="bcWdbCreator__saveSpinner" aria-hidden="true" />}
          </button>
        </footer>
      </div>
    </div>
  );
};

// Renders the small status pill under the email / business-name fields.
// Stays out of the way when the field is empty (`idle`) and only surfaces
// noisy states (`checking`, `exists`, `invalid`) so the form stays uncluttered
// for the happy path. `available` is shown subtly as a green confirmation.
//
// The wrapper uses `key={check.status}` so React fully unmounts the previous
// state and mounts a brand-new element — that prevents the spinner's spin
// animation from briefly bleeding onto the static check / warn icons when
// transitioning between statuses (React would otherwise try to reuse the
// existing DOM nodes because the JSX shape looks similar).
const DuplicateHint = ({ check, kind }) => {
  if (!check || check.status === 'idle') return null;

  let body = null;

  if (check.status === 'invalid') {
    if (kind !== 'email') return null;
    body = (
      <span className="bcWdbCreator__hint invalid" key="invalid">
        <DotIcon />
        Not a valid email address
      </span>
    );
  } else if (check.status === 'checking') {
    body = (
      <span className="bcWdbCreator__hint checking" key="checking">
        <span className="bcWdbCreator__hint-spinner" aria-hidden="true" />
        Checking your database…
      </span>
    );
  } else if (check.status === 'exists') {
    const noun = kind === 'email' ? 'email' : 'business name';
    body = (
      <span className="bcWdbCreator__hint exists" role="alert" key="exists">
        <WarnIcon />
        Already in your database
        {check.label ? <strong> · {check.label}</strong> : null}
        <span className="bcWdbCreator__hint-sub">Adding duplicate {noun} is blocked.</span>
      </span>
    );
  } else if (check.status === 'available') {
    body = (
      <span className="bcWdbCreator__hint available" key="available">
        <CheckIcon />
        Not in your database yet
      </span>
    );
  }

  return body;
};

const DotIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
    <path d="M12 8v5M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const WarnIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 2L1 21h22L12 2z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="M12 9v5M12 18h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Optional field-group that the user added via the picker. Renders the
// children immediately (no collapse), with a discreet "Remove" affordance
// in the top-right corner so the user can clear the entire group if they
// changed their mind.
const OptionalSection = ({ title, onRemove, children }) => (
  <section className="bcWdbCreator__section">
    <div className="bcWdbCreator__section-header">
      <span className="bcWdbCreator__section-title">{title}</span>
      <button
        type="button"
        className="bcWdbCreator__section-remove"
        onClick={onRemove}
        title={`Remove ${title}`}
        aria-label={`Remove ${title}`}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
    </div>
    {children}
  </section>
);

// Icon set for the section-picker chips. Kept local because each chip uses
// exactly one icon and adding new ones means adding a single SVG below.
const SectionChipIcon = ({ name }) => {
  switch (name) {
    case 'address':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
          <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2" />
        </svg>
      );
    case 'social':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="18" cy="5" r="3" stroke="currentColor" strokeWidth="2" />
          <circle cx="6" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
          <circle cx="18" cy="19" r="3" stroke="currentColor" strokeWidth="2" />
          <path d="M8.6 13.5L15.4 17.5M15.4 6.5L8.6 10.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    case 'metrics':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M3 3v18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path d="M7 14l4-4 3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'person':
    default:
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2" />
          <path d="M4 21v-1a6 6 0 016-6h4a6 6 0 016 6v1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
  }
};

export default WorldDbCreator;
